import React, { useState, useRef } from "react";
import { useLongPress } from "ahooks";
import { RocketLaunchIcon } from "@heroicons/react/24/outline";
import { showToast } from "utils/utils";
import { classNames } from "utils/utils";
import { useRound } from "context/roundContext";
import { httpsCallable } from "firebase/functions";
import { useLeague } from "context/leagueContext";
import { useFunctions } from "reactfire";
import { useEventPick } from "context/eventPickContext";
import SkeletonLoader from "components/skeletonLoader";
import { FirebaseAnalytics } from "@capacitor-community/firebase-analytics";

export const PredictButton = ({ outcome, home }) => {
  const [isSwiping, setSwiping] = useState(false);
  const [isBoosting, setIsBoosting] = useState(false);
  const {
    state: { eventData: event, outcomes, isLoading: eventPickLoading },
    dispatch,
  } = useEventPick();

  const {
    state: {
      leagueUserData,
      leagueInfo,
      isLoading: leagueLoading,
      leagueIsPlacingPrediction,
      nextRoundId,
    },
    dispatch: leagueDispatch,
  } = useLeague();

  const {
    state: { currentRound, isLoading: roundLoading },
  } = useRound();

  const refContainer = useRef(null);
  const functions = useFunctions();
  const callPlaceCorrectScorePrediction = httpsCallable(
    functions,
    "placeCorrectScorePrediction"
  );
  const callPlaceCorrectScoreKnockout = httpsCallable(
    functions,
    "placeCorrectScoreKnockout"
  );

  const callPlaceLmsPrediction = httpsCallable(functions, "place1x2Knockout");
  const callPlace1x2Prediction = httpsCallable(functions, "place1x2Prediction");

  const isCorrectScore = leagueInfo?.gameRule == 1 || leagueInfo?.gameRule == 3;
  const isLms = leagueInfo?.gameRule == 2;
  const isNextRound = nextRoundId == currentRound.id;

  let pickData = isCorrectScore
    ? outcomes[home ? "home" : "away"][outcome]
    : outcomes[outcome];
  const isDisabled = pickData?.disabled || (leagueIsPlacingPrediction && isLms);

  useLongPress(
    (e) => {
      if (!isCorrectScore && !isLms) setIsBoosting(true);
    },
    refContainer,
    {
      delay: 600,
      moveThreshold: { y: 30, X: 30 },
      onLongPressEnd: (e) => {
        if (isDisabled) return;
        setSwiping(false);
        placePrediction(!isCorrectScore && !isLms ? true : null, e);
        setIsBoosting(false);
      },
      onClick: (e) => {
        if (isDisabled) return;
        if (isSwiping) {
          setSwiping(false);
          return;
        }
        placePrediction(false, e);
      },
    }
  );

  async function placePredictionCall(callPlacePredictionFn) {
    return callPlacePredictionFn()
      .then((result) => {
        FirebaseAnalytics.logEvent({
          name: "place_prediction_pick",
          params: {
            content_type: "event",
            content_id: event?.id,
          },
        });
        let dispatchData = {};

        if (isCorrectScore) {
          dispatchData = {
            type: "setIsPlacingPredictionCS",
            value: { isPlacing: false, outcome: outcome, home: home },
          };
        } else if (isLms) {
          dispatchData = {
            type: "setIsPlacingPredictionLMS",
            value: { isPlacing: false, outcome: outcome },
          };
          leagueDispatch({
            type: "setIsPlacingPrediction",
            value: { leagueIsPlacingPrediction: false },
          });
        } else {
          dispatchData = {
            type: "setIsPlacingPrediction1x2",
            value: { isPlacing: false, outcome: outcome },
          };
        }
        dispatch(dispatchData);
        return result;
      })
      .catch((error) => {
        showToast(
          error.message == "round_started_error"
            ? "Prediction failed. Round has started"
            : "Something went wrong, please try again."
        );

        // add for all types TODO
        dispatch({
          type: "setIsPlacingPredictionCS",
          value: { isPlacing: false, outcome: outcome, home: home },
        });
        return error;
      });
  }

  async function placePrediction(boosts, e) {
    e.preventDefault();

    let dispatchData = {};

    if (isCorrectScore) {
      dispatchData = {
        type: "setIsPlacingPredictionCS",
        value: { isPlacing: true, outcome: outcome, home: home },
      };
    } else if (isLms) {
      dispatchData = {
        type: "setIsPlacingPredictionLMS",
        value: { isPlacing: true, outcome: outcome },
      };

      leagueDispatch({
        type: "setIsPlacingPrediction",
        value: { leagueIsPlacingPrediction: true },
      });
    } else {
      dispatchData = {
        type: "setIsPlacingPrediction1x2",
        value: { isPlacing: true, outcome: outcome },
      };
    }
    dispatch(dispatchData);

    let data = {
      leagueRef: leagueInfo?.leagueRef,
      seasonRef: leagueInfo?.seasonRef,
      roundId: currentRound?.id,
      userId: leagueUserData.userId,
      prediction: {
        pick: parseInt(outcome),
      },
      leagueId: leagueInfo?.id,
      eventId: event.id,
    };

    if (!isCorrectScore && !isLms) {
      data.prediction.boost = boosts;
    }

    if (isCorrectScore) {
      data.prediction.home = home;
    }

    if (isLms) {
      data.prediction.teamName = outcomes[outcome].label;
    }

    if (leagueInfo?.gameRule == 1) {
      return placePredictionCall(() => callPlaceCorrectScorePrediction(data));
    } else if (leagueInfo?.gameRule == 3) {
      return placePredictionCall(() => callPlaceCorrectScoreKnockout(data));
    } else if (leagueInfo?.gameRule == 0 || leagueInfo?.gameRule == 4) {
      return placePredictionCall(() => callPlace1x2Prediction(data));
    } else if (leagueInfo?.gameRule == 2) {
      return placePredictionCall(() => callPlaceLmsPrediction(data));
    }
  }
  if (leagueLoading || roundLoading || eventPickLoading || eventPickLoading) {
    return (
      <SkeletonLoader type={"predictButton"} amount={1} horizontal={true} />
    );
  }

  const backgroundColorClass = pickData?.predicted
    ? !event?.settled
      ? isLms
        ? "bg-customGreen"
        : pickData?.boost
        ? "bg-[#C5B358]"
        : "bg-black text-white"
      : pickData?.isPartiallyCorrectResult
      ? "bg-yellow-500"
      : pickData?.isCorrectResult
      ? "bg-customGreen"
      : "bg-red-500 text-white"
    : pickData?.isCorrectResult
    ? "border-2 border-black bg-white"
    : event?.settled
    ? "bg-white"
    : isDisabled
    ? "bg-gray-200"
    : "bg-white";

  const animationSubClass = isLms
    ? "animate-pulse !bg-yellow-500"
    : "animate-spinReverse";

  const animationClass = isLms
    ? ""
    : "p-[2px] animate-spin2 bg-gradient-to-r from-transparent to-headerSecondary";

  const lmsButtonClass = home
    ? isNextRound
      ? "rounded-tl-md justify-end"
      : "rounded-l-md justify-end"
    : isNextRound
    ? "rounded-tr-md"
    : "rounded-r-md";

  const outcomeButtonClassNames = classNames(
    backgroundColorClass,
    pickData?.isPlacing && animationSubClass,
    isBoosting && !pickData?.boost && "cursor-wait",
    isLms ? lmsButtonClass : "rounded-full justify-center",
    isLms && "min-w-0",
    "flex w-full h-full items-center font-medium text-sm p-2 select-none"
  );

  return (
    <button
      onTouchMove={(e) => {
        e.preventDefault();
        setSwiping(true);
      }}
      key={outcome + event.id}
      ref={refContainer}
      disabled={isDisabled}
      className={classNames(
        isCorrectScore
          ? "w-7 h-7"
          : isLms
          ? "w-full h-12 grid items-center"
          : "w-10 h-10",
        !isLms && "rounded-full",
        "flex focus:outline-none select-none",
        isDisabled ? "cursor-default" : "cursor-pointer",
        pickData?.isPlacing && animationClass,
        isBoosting && "animate-tiltshake"
      )}
    >
      <div className={outcomeButtonClassNames}>
        {isBoosting ? (
          // <RocketLaunchIcon className="h-12 w-12" />
          <span className="truncate text-sm sm:text-lg  text-[#C5B358]">
            2x
          </span>
        ) : isLms ? (
          <span className="truncate text-sm sm:text-lg">{pickData?.label}</span>
        ) : (
          pickData?.label
        )}
      </div>
    </button>
  );
};
